/** Pagina "Mercati": stato real-time dei mercati mondiali, trigger pre-apertura (SPEC-010 §8.2). */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Button, Card, EmptyState, Spinner, Switch, TableWrap } from '@/ui';
import { RequestBriefingButton } from './RequestBriefingButton';
import { useMarkets, useSetMarketTrigger, type MarketListItem } from './api';
import { formatCountdown, nextEventFor, nextEventSortKey, STATUS_LABELS, STATUS_TONE } from './market-status-format';
import './markets.css';

function TriggerToggle({ market, featureEnabled }: { market: MarketListItem; featureEnabled: boolean }) {
  const setTrigger = useSetMarketTrigger(market.mic);
  if (!featureEnabled) {
    return (
      <span title="Il trigger pre-apertura è disattivato per l'intero server: l'interruttore per-mercato non avrebbe effetto">
        <Switch label="Non disponibile sul server" checked={false} onChange={() => {}} disabled />
      </span>
    );
  }
  return (
    <Switch
      label="Trigger pre-apertura"
      checked={market.triggerEnabled}
      onChange={(checked) => setTrigger.mutate({ enabled: checked })}
    />
  );
}

function MarketRow({ market, triggerFeatureEnabled }: { market: MarketListItem; triggerFeatureEnabled: boolean }) {
  const event = nextEventFor(market);
  return (
    <tr>
      <td>
        <Link to={`/markets/${market.mic}`} className="markets__name-link">
          <span className="markets__name">{market.name}</span>
          <span className="markets__meta">
            {market.mic} · {market.city}
          </span>
        </Link>
      </td>
      <td>
        <Badge tone={STATUS_TONE[market.status]}>{STATUS_LABELS[market.status]}</Badge>
      </td>
      <td className="cell--minor">
        {event ? `${event.label} ${formatCountdown(event.at)}` : 'Sempre aperto'}
      </td>
      <td className="numeric col--secondary">
        {market.watchlistInstrumentCount > 0 ? (
          <Link to={`/markets/${market.mic}`}>{market.watchlistInstrumentCount} titoli</Link>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td>
        <TriggerToggle market={market} featureEnabled={triggerFeatureEnabled} />
      </td>
      <td className="col--secondary">
        <div className="markets__row-actions">
          <RequestBriefingButton
            mic={market.mic}
            name={market.name}
            watchlistInstrumentCount={market.watchlistInstrumentCount}
          />
          <Link to={`/reports?mic=${market.mic}`} className="btn btn--ghost btn--sm">
            Resoconti
          </Link>
        </div>
      </td>
    </tr>
  );
}

function MarketsTable({ markets, triggerFeatureEnabled }: { markets: MarketListItem[]; triggerFeatureEnabled: boolean }) {
  return (
    <TableWrap>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>Mercato</th>
            <th>Stato</th>
            <th>Prossimo evento</th>
            <th className="numeric col--secondary">Titoli in watchlist</th>
            <th>Trigger pre-apertura</th>
            <th className="col--secondary" aria-label="Resoconti" />
          </tr>
        </thead>
        <tbody>
          {markets.map((market) => (
            <MarketRow key={market.mic} market={market} triggerFeatureEnabled={triggerFeatureEnabled} />
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export function MarketsPage() {
  const marketsQuery = useMarkets();
  const [showOthers, setShowOthers] = useState(false);

  return (
    <div className="markets">
      <header className="markets__header">
        <div>
          <h1 className="markets__title">Mercati</h1>
          <p className="markets__subtitle">
            Stato in tempo reale dei principali mercati mondiali, con analisi AI automatica un&apos;ora prima
            dell&apos;apertura per i titoli in watchlist
          </p>
        </div>
      </header>

      {marketsQuery.isPending ? (
        <Spinner label="Caricamento dei mercati…" />
      ) : marketsQuery.isError ? (
        <Alert variant="error" title="Impossibile caricare i mercati">
          {marketsQuery.error instanceof ApiError ? marketsQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      ) : (
        (() => {
          const { triggerFeatureEnabled } = marketsQuery.data;
          const linked = marketsQuery.data.items
            .filter((market) => market.watchlistInstrumentCount > 0)
            .sort((a, b) => nextEventSortKey(a) - nextEventSortKey(b));
          const others = marketsQuery.data.items
            .filter((market) => market.watchlistInstrumentCount === 0)
            .sort((a, b) => nextEventSortKey(a) - nextEventSortKey(b));

          return (
            <>
              {!triggerFeatureEnabled && (
                <Alert variant="info" title="Trigger pre-apertura disattivato per l'intero server">
                  Nessun mercato avvierà l&apos;analisi AI automatica finché la funzionalità non viene riattivata a
                  livello di configurazione (variabile <code>MARKET_OPEN_TRIGGER_ENABLED</code>).
                </Alert>
              )}
              <Card title="I tuoi mercati" subtitle="Mercati con almeno un titolo in watchlist, ordinati per prossimo evento" flush>
                {linked.length === 0 ? (
                  <EmptyState
                    icon="🏛️"
                    title="Nessun titolo in watchlist collegato a un mercato"
                    description="Aggiungi uno strumento alla watchlist: il mercato di quotazione viene risolto automaticamente dall'ISIN."
                  />
                ) : (
                  <MarketsTable markets={linked} triggerFeatureEnabled={triggerFeatureEnabled} />
                )}
              </Card>

              <Card
                title="Altri mercati"
                flush
                action={
                  <Button variant="ghost" onClick={() => setShowOthers((open) => !open)}>
                    {showOthers ? 'Nascondi' : `Mostra (${others.length})`}
                  </Button>
                }
              >
                {showOthers && <MarketsTable markets={others} triggerFeatureEnabled={triggerFeatureEnabled} />}
              </Card>
            </>
          );
        })()
      )}
    </div>
  );
}
